import { useEffect, useState } from 'react'

export interface RecentlyViewedItem {
  id: string
  slug: string
  title: string        // celebrity name or media title
  subtitle: string     // category or year + type
  imageUrl: string | null
  type: 'celebrity' | 'media'
  viewedAt: number     // timestamp
}

const STORAGE_KEY = 'celebrity-picks-recently-viewed'
const MAX_ITEMS = 10

function readFromStorage(): RecentlyViewedItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function writeToStorage(items: RecentlyViewedItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // localStorage may be unavailable (private mode, quota exceeded)
  }
}

/** Add or refresh an item in the recently viewed list. Call on page mount. */
export function trackRecentlyViewed(item: Omit<RecentlyViewedItem, 'viewedAt'>) {
  const existing = readFromStorage().filter((i) => i.id !== item.id)
  const updated = [{ ...item, viewedAt: Date.now() }, ...existing].slice(0, MAX_ITEMS)
  writeToStorage(updated)
}

/** React hook — returns the list, live-updated when called from any page. */
export function useRecentlyViewed() {
  const [items, setItems] = useState<RecentlyViewedItem[]>([])

  useEffect(() => {
    setItems(readFromStorage())

    // Sync across tabs
    function handleStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) setItems(readFromStorage())
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  return items
}
